import type { FilterConfig, SelectedFilters } from '@/models/filters'
import {
  GridToolbarContainer,
  GridToolbarProps,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'
import React from 'react'
import { Box, Chip, IconButton, Tooltip } from '@mui/material'
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material'
import types from '@/pages/Device/DeviceType'

// Fixed Custom Toolbar that extends GridToolbarProps
export interface CustomToolbarProps extends GridToolbarProps {
  onFilterClick?: () => void
  enableFilters?: boolean
  enableSearch?: boolean
  selectedFilters?: SelectedFilters
  filterConfig?: FilterConfig
  onRemoveFilter?: (groupId: string, optionKey: string) => void
  searchValue?: string
  onSearchChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

// Helper function to get display label for a filter option
const getDisplayLabel = (
  groupId: string,
  optionKey: string,
  optionLabel: string,
  filterConfig: FilterConfig,
): string => {
  // Special handling for device type filters
  if (groupId === 'type' && optionKey in types) {
    return types[optionKey as keyof typeof types]
  }

  // For other filters, find the label from filterConfig
  const group = filterConfig.groups.find((g) => g.id === groupId)
  const option = group?.options.find((opt) => opt.key === optionKey)

  return option?.label ?? optionLabel
}

// Helper function to build filter chips
const buildFilterChips = (
  selectedFilters: SelectedFilters,
  filterConfig: FilterConfig,
): Array<{ groupId: string; optionKey: string; label: string }> => {
  const filterChips: Array<{
    groupId: string
    optionKey: string
    label: string
  }> = []

  for (const [groupId, options] of Object.entries(selectedFilters)) {
    const hasGroup = filterConfig.groups.some((g) => g.id === groupId)

    if (hasGroup) {
      for (const [optionKey, optionLabel] of Object.entries(options)) {
        const displayLabel = getDisplayLabel(
          groupId,
          optionKey,
          optionLabel,
          filterConfig,
        )

        filterChips.push({
          groupId,
          optionKey,
          label: displayLabel,
        })
      }
    }
  }

  return filterChips
}

export const CustomToolbar: React.FC<CustomToolbarProps> = (props) => {
  const {
    onFilterClick,
    enableFilters = true,
    enableSearch = true,
    selectedFilters = {},
    filterConfig,
    onRemoveFilter,
    searchValue,
    onSearchChange,
  } = props

  // Get filter chips to display
  const filterChips =
    filterConfig && selectedFilters
      ? buildFilterChips(selectedFilters, filterConfig)
      : []

  const handleClearSearch = () => {
    if (onSearchChange) {
      onSearchChange({
        target: { value: '' },
      } as React.ChangeEvent<HTMLInputElement>)
    }
  }
  return (
    <Box>
      <GridToolbarContainer className="MuiDataGrid-toolbarContainer">
        <Box sx={{ flexGrow: 1 }} />

        {enableFilters && onFilterClick && (
          <Tooltip title="Open Filters">
            <IconButton
              data-testid="toolbar-filter-button"
              onClick={onFilterClick}
              size="small"
              sx={{ mr: 1 }}
            >
              <FilterIcon />
            </IconButton>
          </Tooltip>
        )}
        {enableSearch && (
          <Box className="search-container">
            <GridToolbarQuickFilter
              data-testid="toolbar-search-input"
              debounceMs={0}
              value={searchValue ?? ''}
              onChange={onSearchChange}
              placeholder="Search devices..."
              slotProps={{
                input: {
                  className: 'search-label',
                },
              }}
            />

            {/* Clear button positioned absolutely within the search container */}
            {searchValue?.trim() && (
              <Box className="clear-search-button">
                <IconButton
                  data-testid="toolbar-clear-search-button"
                  size="small"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.54)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        )}
      </GridToolbarContainer>

      {/* Filter Chips Row */}
      {filterChips.length > 0 && enableFilters && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap-reverse',
            justifyContent: 'flex-end',
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {filterChips.map((chip) => (
            <Chip
              key={`${chip.groupId}-${chip.optionKey}`}
              data-testid={`filter-chip-${chip.groupId}-${chip.optionKey}`}
              label={chip.label}
              onDelete={() => onRemoveFilter?.(chip.groupId, chip.optionKey)}
              size="small"
              sx={{
                backgroundColor: 'primary.lighter',
                '& .MuiChip-deleteIcon': {
                  color: 'primary.main',
                  '&:hover': {
                    color: 'primary.dark',
                  },
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
